import React from 'react';
import { Descriptions, Button, Space, Card, Image } from 'antd';
import dayjs from 'dayjs';
import { formatAmountText } from '../../../../utils/numberFormat';
import { DebtTypeOptions, CreditorTypeOptions, EvidenceTypeOptions, labelOfCollectionStatus } from '../../../../constants/debtEnums';

const ReviewStep = ({ value, onPrev, onSubmit, onSaveDraft, onSubmitPublish, config }) => {
  const {
    title,
    debt_type,
    principal_amount,
    interest_amount,
    penalty_amount,
    other_amount,
    total_amount,
    debt_occurrence_date,
    description,
    is_anonymous,
    is_original_creditor,
    disposal_method,
    transfer_pricing_amount,
    transfer_discount_rate,
    creditor,
    debtorsPersonal = [],
    debtorsEnterprise = [],
    evidences = [],
  } = value;
  const showCollectionStatus = String(value?.status || '').toUpperCase() !== 'DRAFT';

  return (
    <div>
      <Descriptions title="复核信息" bordered size="small" column={1}>
        <Descriptions.Item label="债权名称">{title}</Descriptions.Item>
        <Descriptions.Item label="债权类型">{(() => {
          const opts = (config?.debt_type || DebtTypeOptions).map(o => ({ value: String(o.value ?? o.code), label: o.label }));
          const found = opts.find(opt => String(opt.value) === String(debt_type));
          return found ? `${found.value}-${found.label}` : String(debt_type ?? '');
        })()}</Descriptions.Item>
        <Descriptions.Item label="金额">本金{formatAmountText(principal_amount)} 人民币 / 利息{interest_amount}%/月 / 违约金{formatAmountText(penalty_amount)} 人民币 / 其他{formatAmountText(other_amount)} 人民币</Descriptions.Item>
        <Descriptions.Item label="总涉及金额">{formatAmountText(total_amount)} 人民币</Descriptions.Item>
        <Descriptions.Item label="发生日">{(() => {
          if (!debt_occurrence_date) return '';
          const d = dayjs(debt_occurrence_date);
          return d.isValid() ? d.format('YYYY年MM月DD日') : String(debt_occurrence_date);
        })()}</Descriptions.Item>
        <Descriptions.Item label="债权说明">{description}</Descriptions.Item>
        <Descriptions.Item label="匿名">{is_anonymous ? '是' : '否'}</Descriptions.Item>
        <Descriptions.Item label="原始债权人">{is_original_creditor ? '是' : '否'}</Descriptions.Item>
        {showCollectionStatus && (
          <Descriptions.Item label="催收状态">{labelOfCollectionStatus(value?.collection_status)}</Descriptions.Item>
        )}
        <Descriptions.Item label="处置方式">{(() => {
          const opts = (config?.disposal_method || []).length > 0 
            ? (config?.disposal_method || []).map(o => ({ value: String(o.value ?? o.code), label: o.label }))
            : [{ value: 'RETAIN', label: '保留' }, { value: 'OUTSOURCED_COLLECTION', label: '委外催收' }, { value: 'LOW_PRICE_TRANSFER', label: '低价转让' }];
          const found = opts.find(opt => String(opt.value) === String(disposal_method));
          return found ? `${found.value}-${found.label}` : String(disposal_method ?? '');
        })()}</Descriptions.Item>
        {disposal_method === 'LOW_PRICE_TRANSFER' && (
          <Descriptions.Item label="低价转让方式">{(() => {
            const opts = (config?.low_price_option || []).length > 0 
              ? (config?.low_price_option || []).map(o => ({ value: String(o.value ?? o.code), label: o.label }))
              : [{ value: 'PRICING', label: '定价' }, { value: 'DISCOUNT', label: '折扣' }, { value: 'NEGOTIATED', label: '另行商议' }];
            const found = opts.find(opt => String(opt.value) === String(value.low_price_option));
            return found ? `${found.value}-${found.label}` : String(value.low_price_option ?? '');
          })()}</Descriptions.Item>
        )}
        {disposal_method === 'LOW_PRICE_TRANSFER' && value.low_price_option === 'PRICING' && (
          <Descriptions.Item label="定价金额">{formatAmountText(transfer_pricing_amount)}</Descriptions.Item>
        )}
        {disposal_method === 'LOW_PRICE_TRANSFER' && value.low_price_option === 'DISCOUNT' && (
          <Descriptions.Item label="折扣比例">{transfer_discount_rate}%</Descriptions.Item>
        )}
        {disposal_method === 'LOW_PRICE_TRANSFER' && value.low_price_option === 'NEGOTIATED' && (
          <Descriptions.Item label="另行商议">由平台撮合后另行商议具体价格</Descriptions.Item>
        )}
      </Descriptions>

      <Descriptions title="债权人" bordered size="small" column={1} style={{ marginTop: 16 }}>
        <Descriptions.Item label="债权人类型">{(() => {
          const found = CreditorTypeOptions.find(opt => String(opt.value) === String(creditor?.creditor_type));
          return found ? `${found.value}-${found.label}` : String(creditor?.creditor_type ?? '');
        })()}</Descriptions.Item>
        {creditor?.creditor_type === 'P' ? (
          <>
            <Descriptions.Item label="姓名">{creditor?.name}</Descriptions.Item>
            <Descriptions.Item label="身份证号">{creditor?.id_card_no}</Descriptions.Item>
          </>
        ) : (
          <>
            <Descriptions.Item label="企业名称">{creditor?.company_name}</Descriptions.Item>
            <Descriptions.Item label="统一社会信用代码">{creditor?.social_credit_code}</Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="电话">{creditor?.phone}</Descriptions.Item>
        <Descriptions.Item label="邮箱">{creditor?.email}</Descriptions.Item>
        <Descriptions.Item label="省">{creditor?.province}</Descriptions.Item>
        <Descriptions.Item label="市">{creditor?.city}</Descriptions.Item>
        <Descriptions.Item label="详细地址">{creditor?.address}</Descriptions.Item>
      </Descriptions>

      <Descriptions title={`债务人（${debtorsPersonal.length + debtorsEnterprise.length}）`} bordered size="small" column={1} style={{ marginTop: 16 }}>
        <Descriptions.Item label="个人债务人">{debtorsPersonal.length}</Descriptions.Item>
        <Descriptions.Item label="企业债务人">{debtorsEnterprise.length}</Descriptions.Item>
      </Descriptions>

      {debtorsPersonal.length > 0 && (
        <Card title="个人债务人详情" size="small" style={{ marginTop: 12 }}>
          {debtorsPersonal.map((d, idx) => (
            <Descriptions key={`p-${idx}`} bordered size="small" column={1} style={{ marginBottom: 8 }}>
              <Descriptions.Item label="姓名">{d?.name}</Descriptions.Item>
              <Descriptions.Item label="证件号">{d?.id_no}</Descriptions.Item>
              <Descriptions.Item label="电话">{d?.phone}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{d?.email}</Descriptions.Item>
              <Descriptions.Item label="省">{d?.province}</Descriptions.Item>
              <Descriptions.Item label="市">{d?.city}</Descriptions.Item>
              <Descriptions.Item label="详细地址">{d?.address}</Descriptions.Item>
            </Descriptions>
          ))}
        </Card>
      )}

      {debtorsEnterprise.length > 0 && (
        <Card title="企业债务人详情" size="small" style={{ marginTop: 12 }}>
          {debtorsEnterprise.map((d, idx) => (
            <Descriptions key={`e-${idx}`} bordered size="small" column={1} style={{ marginBottom: 8 }}>
              <Descriptions.Item label="企业名称">{d?.company_name}</Descriptions.Item>
              <Descriptions.Item label="统一社会信用代码">{d?.social_credit_code}</Descriptions.Item>
              <Descriptions.Item label="法定代表人姓名">{d?.legal_person_name}</Descriptions.Item>
              <Descriptions.Item label="法定代表人身份证号">{d?.legal_person_id_no}</Descriptions.Item>
              <Descriptions.Item label="电话">{d?.phone}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{d?.email}</Descriptions.Item>
              <Descriptions.Item label="省">{d?.province}</Descriptions.Item>
              <Descriptions.Item label="市">{d?.city}</Descriptions.Item>
              <Descriptions.Item label="详细地址">{d?.address}</Descriptions.Item>
            </Descriptions>
          ))}
        </Card>
      )}

      <Descriptions title={`证据材料（${evidences.length}）`} bordered size="small" column={1} style={{ marginTop: 16 }}>
        {evidences.map((e, idx) => {
          const opts = (config?.evidence_type || []).length > 0 
            ? (config?.evidence_type || []).map(o => ({ value: String(o.value ?? o.code), label: o.label }))
            : EvidenceTypeOptions.map(o => ({ value: String(o.code), label: o.label }));
          const found = opts.find(opt => String(opt.value) === String(e?.evidence_type));
          const label = found ? `${found.value}-${found.label}` : String(e?.evidence_type || '');
          const isImg = (() => {
            const name = String(e?.file_url || '').toLowerCase();
            return name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png');
          })();
          return (
            <Descriptions.Item key={idx} label={`证据${idx + 1}`}>
              <div>
                <div>{label}</div>
                <div>
                  {e?.file_url ? (
                    e?.preview_url ? (
                      <a href={e?.preview_url} target="_blank" rel="noreferrer">{String(e.file_url).split('/').pop()}</a>
                    ) : (
                      <>{String(e.file_url).split('/').pop()}</>
                    )
                  ) : null}
                </div>
                {isImg && e?.preview_url && (
                  <div style={{ marginTop: 8 }}>
                    <Image src={e.preview_url} width={160} />
                  </div>
                )}
              </div>
            </Descriptions.Item>
          );
        })}
      </Descriptions>

      <div style={{ marginTop: 16 }}>
        <Space>
          <Button onClick={onPrev}>上一步</Button>
          {/* <Button type="primary" onClick={onSubmit}>提交审核</Button> */}
          <Button onClick={onSaveDraft}>保存草稿</Button>
          <Button type="primary" onClick={onSubmitPublish}>发布债权</Button>
        </Space>
      </div>
    </div>
  );
};

export default ReviewStep;
